import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Paper,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Typography,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../api';
import { PREMIUM_FEATURES } from '../constants/premiumFeatures';
import { useAuth } from '../contexts/AuthContext';
import { hasActivePaidFeature } from '../utils/premiumAccess';
import { calculateReferralPrice, getDepartmentAdjustedPricing } from '../utils/referralPricing';

declare global {
  interface Window {
    PaystackPop?: {
      setup: (config: any) => {
        openIframe: () => void;
      };
    };
  }
}

const formatNaira = (value: number) => `₦${Number(value || 0).toLocaleString('en-NG')}`;
const applyDiscount = (featureKeys: string[], pricing: Record<string, number | string | undefined>, percent: number, discountedFeatureLimit = featureKeys.length) => {
  const credits = Object.fromEntries(featureKeys.map((featureKey, index) => [
    featureKey,
    index < discountedFeatureLimit ? 1 : 0,
  ]));
  return calculateReferralPrice(featureKeys, pricing, credits, percent);
};

const Payment: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, updateUser } = useAuth();
  const [pricing, setPricing] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paystackReady, setPaystackReady] = useState(false);
  const [paymentSuccessOpen, setPaymentSuccessOpen] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [referralBenefits, setReferralBenefits] = useState<any>({ rewards: [], welcomeDiscount: { eligible: false } });
  const [selectedRewardId, setSelectedRewardId] = useState('');
  const [useWelcomeDiscount, setUseWelcomeDiscount] = useState(false);

  const routeState = (location.state as any) || {};
  const userId = user?._id;
  const allAvailableFeatures = PREMIUM_FEATURES.filter((feature) => !hasActivePaidFeature(user?.paidFeatures, user?.paidFeatureExpiries, feature.key));
  const departmentPricing = useMemo(
    () => getDepartmentAdjustedPricing(pricing?.individualAccess || {}, user?.department),
    [pricing?.individualAccess, user?.department]
  );
  const initialSelection = Array.isArray(routeState.selectedFeatures)
    ? routeState.selectedFeatures.filter((feature: string) => !hasActivePaidFeature(user?.paidFeatures, user?.paidFeatureExpiries, feature))
    : routeState.featureKey
      ? [routeState.featureKey].filter((feature) => !hasActivePaidFeature(user?.paidFeatures, user?.paidFeatureExpiries, feature))
      : [];

  const [selectedFeatures, setSelectedFeatures] = useState<string[]>(() => initialSelection);

  useEffect(() => {
    const cleanSelection = selectedFeatures.filter((feature) => !hasActivePaidFeature(user?.paidFeatures, user?.paidFeatureExpiries, feature));
    if (cleanSelection.length !== selectedFeatures.length) {
      setSelectedFeatures(cleanSelection);
    }
  }, [selectedFeatures, user?.paidFeatures, user?.paidFeatureExpiries]);

  useEffect(() => {
    const scriptId = 'paystack-inline-script';
    const existing = document.getElementById(scriptId) as HTMLScriptElement | null;

    if (!existing) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://js.paystack.co/v1/inline.js';
      script.async = true;
      script.onload = () => {
        console.log('Paystack script loaded successfully');
        setPaystackReady(true);
      };
      script.onerror = () => {
        console.error('Failed to load Paystack script');
        setError('Failed to load Paystack payment gateway. Please refresh the page and try again.');
        setPaystackReady(false);
      };
      document.body.appendChild(script);
    } else {
      // Script already exists, mark as ready if PaystackPop is available
      if (window.PaystackPop) {
        setPaystackReady(true);
      }
    }
  }, []);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await api.get('/site-settings');
        setPricing(res.data?.data || {});
      } catch (err) {
        console.error('Failed to load access pricing', err);
        setError('Unable to load pricing information. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  useEffect(() => {
    if (!userId) return;
    api.get('/users/referral-benefits')
      .then((response) => {
        const benefits = response.data?.data || { rewards: [], welcomeDiscount: { eligible: false } };
        setReferralBenefits(benefits);
        setUseWelcomeDiscount(Boolean(benefits.welcomeDiscount?.eligible));
      })
      .catch((requestError) => {
        console.error('Failed to load referral benefits', requestError);
        setError('Unable to load referral discounts. Please refresh and try again.');
      });
  }, [userId]);

  const paymentQuote = useMemo(() => {
    const features = selectedFeatures.filter((featureKey) => allAvailableFeatures.some((feature) => feature.key === featureKey));
    const featurePricing = departmentPricing;
    const reward = referralBenefits.rewards.find((item: any) => item._id === selectedRewardId);
    if (reward) return applyDiscount(features, featurePricing, Number(reward.discountPercent));
    if (useWelcomeDiscount && referralBenefits.welcomeDiscount?.eligible) {
      return applyDiscount(features, featurePricing, Number(referralBenefits.welcomeDiscount.percent), 1);
    }
    return calculateReferralPrice(
      features,
      featurePricing,
      user?.referralDiscountCredits || {},
      user?.referralDiscountPercent || 5
    );
  }, [allAvailableFeatures, selectedFeatures, departmentPricing, user?.referralDiscountCredits, user?.referralDiscountPercent, referralBenefits, selectedRewardId, useWelcomeDiscount]);
  const payableAmount = paymentQuote.amount;

  const isPaymentDisabled = loading || submitting || !selectedFeatures.length || payableAmount <= 0 || allAvailableFeatures.length === 0 || !paystackReady || paymentCompleted;

  const toggleFeature = (featureKey: string) => {
    setSelectedFeatures((prev) =>
      prev.includes(featureKey)
        ? prev.filter((item) => item !== featureKey)
        : [...prev, featureKey]
    );
  };

  const handlePayment = async () => {
    let rewardReservationToken = '';
    let rewardReservationReference = '';
    try {
      if (!user) {
        navigate('/login');
        return;
      }

      if (!selectedFeatures.length || payableAmount <= 0) {
        setError('Please choose at least one premium feature and confirm the configured amount.');
        return;
      }

      const publicKey = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY || '';
      if (!publicKey) {
        setError('Paystack public key is not configured. Please add REACT_APP_PAYSTACK_PUBLIC_KEY to your frontend environment.');
        return;
      }

      if (!window.PaystackPop) {
        setError('Paystack checkout is still loading. Please try again in a moment.');
        return;
      }

      setSubmitting(true);
      setError(null);
      setPaymentCompleted(false);

      const reference = `a3tech_${Date.now()}_${Math.random().toString(16).slice(2)}`;
      rewardReservationReference = reference;
      if (selectedRewardId) {
        const reservation = await api.post('/users/referral-benefits/reserve', { rewardId: selectedRewardId, reference });
        rewardReservationToken = reservation.data?.data?.reservationToken || '';
        if (!rewardReservationToken) throw new Error('The selected referral reward could not be reserved.');
      }
      let paymentHandled = false;

      const handlePaystackSuccess = function (response: any) {
        paymentHandled = true;
        console.log('Paystack payment successful:', response);
        verifyPaymentAfterClose(response?.reference || reference, rewardReservationToken)
          .then(() => {
            console.log('Payment verification completed successfully');
          })
          .catch((err) => {
            console.error('Error verifying payment:', err);
            setError(err?.response?.data?.message || 'Unable to verify payment. Please check your payment status.');
            setSubmitting(false);
          });
      };

      const handlePaystackClose = function () {
        if (paymentHandled) return;
        if (selectedRewardId && rewardReservationToken) {
          void api.post('/users/referral-benefits/release', {
            rewardId: selectedRewardId,
            reservationToken: rewardReservationToken,
          }).catch((releaseError) => {
            console.error('Unable to release cancelled referral reward reservation', releaseError);
          });
        }
        console.log('Payment modal closed before completion');
        setSubmitting(false);
        setError('Payment was cancelled before completion.');
      };

      const paystackConfig: any = {
        key: publicKey,
        email: user.email,
        amount: Math.round(payableAmount * 100),
        currency: 'NGN',
        ref: reference,
        metadata: {
          custom_fields: [
            {
              display_name: 'Premium features',
              variable_name: 'premium_features',
              value: selectedFeatures.join(', '),
            },
          ],
        },
        callback: handlePaystackSuccess,
        onClose: handlePaystackClose,
      };

      const paystack = window.PaystackPop.setup(paystackConfig);
      paystack.openIframe();
    } catch (err: any) {
      console.error('Payment initiation error:', err);
      if (selectedRewardId && rewardReservationToken && rewardReservationReference) {
        void api.post('/users/referral-benefits/release', {
          rewardId: selectedRewardId,
          reservationToken: rewardReservationToken,
        }).catch((releaseError) => {
          console.error('Unable to release referral reward after checkout setup failed', releaseError);
        });
      }
      setError(err?.message || 'An error occurred while initiating payment. Please try again.');
      setSubmitting(false);
    }
  };

  const handleGoHome = () => {
    setPaymentSuccessOpen(false);
    setSelectedFeatures([]);
    setError(null);
    setSubmitting(false);
    setPaymentCompleted(false);
    navigate('/', { replace: true });
  };

  const handleReturnHome = () => {
    handleGoHome();
  };

  const handleMakeAnotherPayment = () => {
    setPaymentSuccessOpen(false);
    setSelectedFeatures([]);
    setError(null);
    setSubmitting(false);
    setPaymentCompleted(false);
    setSelectedRewardId('');
  };

  const verifyPaymentAfterClose = async (reference: string, rewardReservationToken = '') => {
    try {
      console.log('Verifying payment with reference:', reference);
      const res = await api.post('/users/pay-access', {
        amount: payableAmount,
        selectedFeatures,
        reference,
        paymentProvider: 'paystack',
        selectedRewardId: selectedRewardId || undefined,
        rewardReservationToken: rewardReservationToken || undefined,
        useWelcomeDiscount: !selectedRewardId && useWelcomeDiscount,
      });

      console.log('Backend verification successful:', res.data);
      const baseUser = user ?? {
        _id: '',
        name: '',
        email: '',
        paidFeatures: {},
      };
      const updatedUser = res.data?.data || {
        ...baseUser,
        paidFeatures: {
          ...(baseUser.paidFeatures || {}),
          ...Object.fromEntries(selectedFeatures.map((feature) => [feature, true])),
        },
      };
      updateUser(updatedUser as any);
      setReferralBenefits((current: any) => ({
        ...current,
        rewards: current.rewards.filter((reward: any) => reward._id !== selectedRewardId),
        welcomeDiscount: selectedRewardId || !useWelcomeDiscount
          ? current.welcomeDiscount
          : { ...current.welcomeDiscount, eligible: false },
      }));
      setSelectedRewardId('');
      setSubmitting(false);
      setError(null);
      setPaymentCompleted(true);
      setPaymentSuccessOpen(true);
    } catch (err: any) {
      console.error('Premium payment verification failed', err);
      setSubmitting(false);
      if (err?.code === 'ECONNABORTED' || !err?.response) {
        setError('Unable to connect to server. Please check your internet connection and try again.');
      } else if (err?.response?.status === 400) {
        setError('Payment was not completed or verified. Please try again.');
      } else if (err?.response?.status === 401) {
        setError('Your session expired. Please log in again.');
      } else {
        setError(err?.response?.data?.message || 'Unable to verify payment. Please try again.');
      }
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 8, mb: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          Premium Access Payment
        </Typography>

        <Typography variant="body1" paragraph>
          Select the premium feature(s) you want to unlock. Individual feature access lasts 30 days after verified payment.
        </Typography>

        <FormGroup sx={{ mt: 2, mb: 3 }}>
          {allAvailableFeatures.length === 0 ? (
            <Typography variant="body1" color="text.secondary">
              You have already paid for all available premium features.
            </Typography>
          ) : (
            allAvailableFeatures.map((feature) => (
              <FormControlLabel
                key={feature.key}
                control={
                  <Checkbox
                    checked={selectedFeatures.includes(feature.key)}
                    onChange={() => toggleFeature(feature.key)}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    <Typography component="span" sx={{ fontWeight: 500 }}>
                      {feature.label} — {formatNaira(Number(departmentPricing[feature.key] ?? 0))}
                      {(Number(user?.referralDiscountCredits?.[feature.key]) || 0) > 0 && (
                        <> → {formatNaira(calculateReferralPrice([feature.key], departmentPricing, user?.referralDiscountCredits || {}, user?.referralDiscountPercent || 5).amount)}</>
                      )}
                      {feature.key === 'labs' && ['commercial', 'art'].includes(String(user?.department || '').toLowerCase()) && ' · 35% department rate'}
                    </Typography>
                    <Typography component="span" variant="caption" color="text.secondary">
                      {feature.description}{(Number(user?.referralDiscountCredits?.[feature.key]) || 0) > 0 ? ` · ${user?.referralDiscountPercent || 5}% referral discount available` : ''}
                    </Typography>
                  </Box>
                }
              />
            ))
          )}
        </FormGroup>

        {(referralBenefits.welcomeDiscount?.eligible || referralBenefits.rewards.length > 0) && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            {referralBenefits.rewards.length > 0 && (
              <FormControl fullWidth>
                <InputLabel id="referral-reward-label">Referral reward</InputLabel>
                <Select
                  labelId="referral-reward-label"
                  label="Referral reward"
                  value={selectedRewardId}
                  onChange={(event) => setSelectedRewardId(event.target.value)}
                >
                  <MenuItem value="">No referral reward</MenuItem>
                  {referralBenefits.rewards.map((reward: any) => (
                    <MenuItem key={reward._id} value={reward._id}>
                      {reward.discountPercent}% reward — milestone {reward.milestone}, expires {new Date(reward.expiresAt).toLocaleDateString()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {referralBenefits.welcomeDiscount?.eligible && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!selectedRewardId && useWelcomeDiscount}
                    disabled={Boolean(selectedRewardId)}
                    onChange={(event) => setUseWelcomeDiscount(event.target.checked)}
                  />
                }
                label={`${referralBenefits.welcomeDiscount.percent}% welcome discount on the first selected feature (expires ${new Date(referralBenefits.welcomeDiscount.expiresAt).toLocaleDateString()})`}
              />
            )}
            {selectedRewardId && referralBenefits.welcomeDiscount?.eligible && (
              <Typography variant="caption" color="text.secondary">
                One discount can be used per payment. Selecting a referral reward will not also consume the welcome discount.
              </Typography>
            )}
          </Box>
        )}

        <Box sx={{ my: 3, p: 3, borderRadius: 2, bgcolor: 'rgba(25,118,210,0.04)', border: '1px solid rgba(25,118,210,0.15)' }}>
          <Typography variant="h6" gutterBottom>Payable amount</Typography>
          <Typography variant="h3" color="primary" sx={{ fontWeight: 700 }}>
            {loading ? 'Loading...' : formatNaira(payableAmount)}
          </Typography>
          {paymentQuote.savings > 0 && (
            <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
              Referral savings: {formatNaira(paymentQuote.savings)}
            </Typography>
          )}
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 3 }}>
          <Button
            variant="contained"
            size="large"
            onClick={handlePayment}
            disabled={isPaymentDisabled}
          >
            {submitting ? 'Processing...' : 'Make payment'}
          </Button>
          <Button variant="outlined" size="large" onClick={handleReturnHome}>
            Return to Home
          </Button>
        </Box>

        {user && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 3, display: 'block' }}>
            You are signed in as {user.email}.
          </Typography>
        )}
      </Paper>

      <Dialog
        open={paymentSuccessOpen}
        onClose={() => setPaymentSuccessOpen(false)}
        aria-labelledby="payment-success-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="payment-success-title">Payment successful</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your premium access has been activated. Would you like to return to the home page or make another payment?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleMakeAnotherPayment} color="primary" variant="outlined">
            Make another payment
          </Button>
          <Button
            onClick={handleGoHome}
            color="primary"
            variant="contained"
            autoFocus
          >
            Go to Home
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Payment;
